import { DIRECTORY, DOCUMENT } from 'constants/documents';
import {
  DocumentEntity,
  DocumentHierarchyItem,
  DocumentTreeRes,
} from 'interfaces/documents';
import { EntityStorages } from 'types';
import {
  DocumentFolderNodeData,
  DocumentNode,
  DocumentNodeData,
  DocumentObjectNodeData,
} from 'types/documents';

export const parseDocNodeID = (nodeID: string) => {
  const [type, id] = nodeID.split('-');
  return { type: type, id: Number(id) };
};

export const createDocNodeID = (type: string, id: number) => `${type}-${id}`;

export const getLastPathElement = (fullPath: string) =>
  fullPath.split('/').pop() || 'Документы';

export const getShortenedPath = (fullPath: string) => {
  const validPathItems = fullPath.split('/').filter((i) => !!i);
  if (validPathItems.length > 3) {
    return [validPathItems[0], '...', ...validPathItems.slice(-2)].join('/');
  } else {
    return validPathItems.join('/');
  }
};

export const getDocumentEntitiesTree = (
  data: DocumentTreeRes,
  activeNodes: DocumentNode[]
): DocumentNodeData[] => {
  if (!data || !data.hierarchy) return [];

  const nonFlatMode = data.hierarchy.some((i) => i.children.length > 0);

  const itemsMap = Object.fromEntries([
    ...data.directories.map((dir) => [createDocNodeID(DIRECTORY, dir.id), dir]),
    ...data.documents.map((doc) => [createDocNodeID(DOCUMENT, doc.id), doc]),
  ]);

  const buildTree = (
    hierarchy: DocumentHierarchyItem,
    level: number
  ): DocumentNodeData[] => {
    const id = createDocNodeID(hierarchy.type, hierarchy.id);
    const item = itemsMap[id] as DocumentEntity;

    if (!item) return [];

    const isFolder = hierarchy.type === DIRECTORY;
    const isObject = hierarchy.type === DOCUMENT;
    const parentID = createDocNodeID(DIRECTORY, hierarchy.parentID);
    const name = nonFlatMode
      ? getLastPathElement(item.name)
      : getShortenedPath(item.name);

    if (isFolder) {
      const folderNode: DocumentFolderNodeData = {
        id: id,
        name: name,
        type: 'folder',
        isFolder: true,
        info: {
          level: level,
          parent: parentID,
          stats: {
            selectedObjectsCount: 0,
            totalObjectsCount: 0,
          },
        },
        state: {
          storage: EntityStorages.NONE,
          selected: activeNodes.some((n) => n.id === id),
        },
        entity: item,
        children: hierarchy.children.flatMap((child) =>
          buildTree(child, level + 1)
        ),
      };
      return [folderNode];
    }

    if (isObject) {
      const objectNode: DocumentObjectNodeData = {
        id: id,
        name: name,
        type: 'object',
        isFolder: false,
        info: {
          level: level,
          parent: parentID,
        },
        state: {
          storage: EntityStorages.NONE,
          selected: activeNodes.some((n) => n.id === id),
        },
        entity: item,
      };
      return [objectNode];
    }

    return [];
  };

  return data.hierarchy.flatMap((hierarchyItem) => buildTree(hierarchyItem, 0));
};

export const updateDocumentTreeSelection = (
  tree: DocumentNodeData[],
  activeNodes: DocumentNode[]
): DocumentNodeData[] => {
  return tree.map((node) => {
    const isSelected = activeNodes.some((n) => n.id === node.id);
    const updatedNode = {
      ...node,
      state: { ...node.state, selected: isSelected },
    };

    if (node.isFolder && node.children) {
      updatedNode.children = updateDocumentTreeSelection(
        node.children,
        activeNodes
      );
    }

    return updatedNode;
  });
};

export const updateDocumentsTree = (
  documentsTree: DocumentNodeData[],
  updatedTree: DocumentNodeData[]
): DocumentNodeData[] => {
  const updateNode = (
    node: DocumentNodeData,
    updatedNode: DocumentNodeData
  ): DocumentNodeData => {
    if (node.id === updatedNode.id) {
      return updatedNode;
    }

    if (node.isFolder) {
      return {
        ...node,
        children: (node.children || []).map((child) =>
          updateNode(child, updatedNode)
        ),
      };
    }
    return node;
  };

  const traverseNodes = (nodes: DocumentNodeData[]): DocumentNodeData[] => {
    return nodes.map((node) => {
      const updatedNode = updatedTree.find((updated) => updated.id === node.id);
      if (updatedNode) {
        node = updateNode(node, updatedNode);
      }

      if (node.isFolder && node.children) {
        node.children = traverseNodes(node.children);
      }

      return node;
    });
  };

  return traverseNodes(documentsTree);
};
